import { Check, CloudSnow, ExternalLink, Heart, MapPinned, MessageCircle, MountainSnow, Route } from 'lucide-react';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { Modal } from '../components/Modal';
import { SectionHeading } from '../components/SectionHeading';
import { Sticker } from '../components/Sticker';
import { birthdayConfig, type Tour } from '../config';
import { useLocalStorage } from '../hooks/useLocalStorage';

type Props = {
  selectedTourId: string;
  onSelectTour: (id: string) => void;
};

export function ToursSection({ selectedTourId, onSelectTour }: Props) {
  const [filter, setFilter] = useState('All');
  const [details, setDetails] = useState<Tour | null>(null);
  const [favourites, setFavourites] = useLocalStorage<string[]>('birthday-favourite-tours', []);

  const visibleTours = useMemo(() => birthdayConfig.tours.filter((tour) => filter === 'All' || tour.filters.includes(filter)), [filter]);
  const selectedTour = birthdayConfig.tours.find((tour) => tour.id === selectedTourId) ?? null;
  const cleanNumber = birthdayConfig.sender.whatsappNumber.replace(/\D/g, '');
  const tourMessage = selectedTour
    ? `Hi! I choose our December adventure 💖\n\nTrip: ${selectedTour.title}\nDestination: ${selectedTour.destination}\nSuggested duration: ${selectedTour.duration}\nHighlights: ${selectedTour.highlights.join(', ')}\n\nLet us confirm the route, weather, transport and hotel before booking.`
    : 'Hi! I am still choosing our December birthday trip 💖';
  const whatsappUrl = cleanNumber && !cleanNumber.includes('X')
    ? `https://wa.me/${cleanNumber}?text=${encodeURIComponent(tourMessage)}`
    : `https://wa.me/?text=${encodeURIComponent(tourMessage)}`;

  const selectTour = (tour: Tour) => {
    onSelectTour(tour.id);
    window.setTimeout(() => document.getElementById('tour-summary')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  };

  return (
    <section id="trips" className="section-shell tours-section">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.tours.eyebrow}
          title={birthdayConfig.copy.tours.title}
          description={birthdayConfig.copy.tours.description}
        />

        <div className="filter-chips" aria-label="Filter December tour ideas">
          {birthdayConfig.copy.tours.filters.map((item) => <button key={item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>{item}</button>)}
        </div>

        <div className="tour-grid">
          {visibleTours.map((tour, index) => {
            const selected = selectedTourId === tour.id;
            const favourite = favourites.includes(tour.id);
            return (
              <motion.article
                key={tour.id}
                className={`tour-card ${selected ? 'selected' : ''}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ delay: (index % 3) * 0.08 }}
              >
                <div className="tour-image-wrap">
                  <ImageWithFallback src={tour.image} alt={`${tour.destination} in winter`} className="tour-image" loading="lazy" />
                  <span className="duration-badge">{tour.duration}</span>
                  {selected && <span className="selected-badge"><Check size={15} /> Selected</span>}
                  <button
                    className={`favourite-button ${favourite ? 'active' : ''}`}
                    onClick={() => setFavourites(favourite ? favourites.filter((id) => id !== tour.id) : [...favourites, tour.id])}
                    aria-label={favourite ? `Remove ${tour.title} from favourites` : `Add ${tour.title} to favourites`}
                  ><Heart size={20} fill={favourite ? 'currentColor' : 'none'} /></button>
                </div>
                <div className="tour-content">
                  <span className="tour-destination"><MapPinned size={16} /> {tour.destination}</span>
                  <h3>{tour.title}</h3>
                  <p>{tour.description}</p>
                  <div className="travel-badges">
                    <span><CloudSnow size={15} /> {tour.weatherBadge}</span>
                    <span><Route size={15} /> {tour.difficultyBadge}</span>
                  </div>
                  <ul>{tour.highlights.slice(0, 4).map((highlight) => <li key={highlight}>{highlight}</li>)}</ul>
                  <div className="tour-tag">{tour.tag}</div>
                  <div className="card-actions">
                    <button className="secondary-button" onClick={() => setDetails(tour)}>View Plan</button>
                    <button className="primary-button" onClick={() => selectTour(tour)}>{selected ? 'Trip Selected' : 'Choose This Trip'}</button>
                  </div>
                </div>
                {selected && <Sticker src="/stickers/mochi-heart.svg" alt="Mochi cat celebrating trip selection" className="tour-selection-sticker" />}
              </motion.article>
            );
          })}
        </div>

        <motion.aside id="tour-summary" className="tour-summary glass" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="summary-icon"><MountainSnow /></div>
          <div>
            <span className="eyebrow">Saved locally</span>
            <h3>{birthdayConfig.copy.tours.summaryTitle}</h3>
            {selectedTour ? (
              <><strong>{selectedTour.title}</strong><p>{selectedTour.duration} • {selectedTour.tag}</p></>
            ) : <p>No trip selected yet. Pick the destination that feels most exciting.</p>}
          </div>
          <a className="primary-button" href={whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle size={18} /> Share Our Trip Choice</a>
        </motion.aside>

        <div className="travel-notice">
          {birthdayConfig.copy.tours.notice}
        </div>
      </div>

      <Modal open={Boolean(details)} onClose={() => setDetails(null)} title={details?.title ?? 'Tour itinerary'} wide>
        {details && (
          <div className="details-modal-content itinerary-modal">
            <ImageWithFallback src={details.image} alt={details.destination} className="itinerary-hero" />
            <span className="eyebrow">Suggested December itinerary</span>
            <h2>{details.title}</h2>
            <p>{details.description}</p>
            <div className="travel-badges"><span><CloudSnow size={15} /> Indicative: {details.weatherBadge}</span><span><Route size={15} /> {details.difficultyBadge}</span></div>
            <div className="itinerary-days">
              {details.itinerary.map((day) => (
                <article key={day.day}><h3>{day.day}</h3><ul>{day.activities.map((activity) => <li key={activity}>{activity}</li>)}</ul></article>
              ))}
            </div>
            <div className="modal-notice">This is a planning idea, not a live booking or availability confirmation. Verify transport, road access, weather and accommodation before payment.</div>
            <div className="card-actions">
              <a className="secondary-button" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(details.destination)}`} target="_blank" rel="noreferrer"><ExternalLink size={18} /> Explore destination</a>
              <button className="primary-button" onClick={() => { selectTour(details); setDetails(null); }}>Choose This Trip</button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
